import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CalendarDays, Mail, Phone, X } from 'lucide-react';
import './CtaSection.css';
import './CtaSection.motion.css';
import useInViewOnce from './hooks/useInViewOnce';
import usePointerParallax from './hooks/usePointerParallax';
import { CONTACT } from './profile';
import { trackEvent } from './analytics';

import glow1 from './images/design/cta-glow-1.svg';
import glow2 from './images/design/cta-glow-2.svg';
import glowDot from './images/design/cta-dot.svg';
import plane from './images/design/cta-plane.svg';

// Same artwork as images/design/cta-trail.svg, inlined so the dotted path can be "drawn":
// the dashed stroke is revealed through a mask whose solid stroke grows (stroke-dashoffset)
// from the glowing dot up to the plane, then a short light runs along it now and then.
const TRAIL_D = 'M160 6C130 40 80 30 70 62C62 88 100 98 90 78C80 58 40 84 8 124';
const TRAIL_D_FROM_DOT = 'M8 124C40 84 80 58 90 78C100 98 62 88 70 62C80 30 130 40 160 6';

const FlightTrail = ({ style }) => (
    <svg
        className="cta-trail"
        style={style}
        width="170"
        height="130"
        viewBox="0 0 170 130"
        preserveAspectRatio="none"
        overflow="visible"
        fill="none"
        aria-hidden="true"
    >
        <defs>
            <mask id="cta-trail-draw" maskUnits="userSpaceOnUse" x="-10" y="-10" width="190" height="150">
                <path className="cta-trail-draw" d={TRAIL_D_FROM_DOT} pathLength="1" stroke="#fff" strokeWidth="8" strokeLinecap="round" />
            </mask>
            <mask id="cta-trail-light" maskUnits="userSpaceOnUse" x="-10" y="-10" width="190" height="150">
                <path className="cta-trail-light" d={TRAIL_D_FROM_DOT} pathLength="1" stroke="#fff" strokeWidth="8" strokeLinecap="round" />
            </mask>
        </defs>
        <g mask="url(#cta-trail-draw)">
            <path d={TRAIL_D} stroke="#3B82F6" strokeOpacity="0.7" strokeWidth="1.6" strokeLinecap="round" strokeDasharray="3 6" />
        </g>
        <path
            className="cta-trail-glint"
            mask="url(#cta-trail-light)"
            d={TRAIL_D}
            stroke="#93C5FD"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeDasharray="3 6"
        />
    </svg>
);

const FORMSPREE_URL = 'https://formspree.io/f/meeblrlo';

const ContactModal = ({ onClose }) => {
    const { t } = useTranslation();
    const [status, setStatus] = useState('idle');

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');
        try {
            const res = await fetch(FORMSPREE_URL, {
                method: 'POST',
                body: new FormData(e.target),
                headers: { Accept: 'application/json' },
            });
            if (res.ok) {
                setStatus('success');
                trackEvent('Contact Form Sent');
                e.target.reset();
            } else {
                setStatus('error');
            }
        } catch {
            setStatus('error');
        }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div
                className="modal-panel contact-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="contact-title"
                onClick={e => e.stopPropagation()}
            >
                <button className="modal-close" onClick={onClose} aria-label={t('common.close')}>
                    <X size={18} />
                </button>

                <form className="contact-form" onSubmit={handleSubmit}>
                    <span className="eyebrow">{t('cta.eyebrow')}</span>
                    <h2 id="contact-title" className="modal-title">{t('contact.form_title')}</h2>
                    <p className="modal-subtitle contact-intro">{t('contact.form_intro')}</p>

                    <div className="contact-direct">
                        <a href={`mailto:${CONTACT.email}`}><Mail size={15} />{CONTACT.email}</a>
                        <a href={`tel:${CONTACT.phone.replace(/\s/g, '')}`} dir="ltr"><Phone size={15} />{CONTACT.phone}</a>
                    </div>

                    {status === 'success' && <div className="form-feedback success" role="status">{t('contact.success')}</div>}
                    {status === 'error' && <div className="form-feedback error" role="alert">{t('contact.error')}</div>}

                    <div className="form-row">
                        <label className="form-field">
                            <span>{t('contact.name')}</span>
                            <input type="text" name="name" placeholder={t('contact.name_placeholder')} required />
                        </label>
                        <label className="form-field">
                            <span>{t('contact.email')}</span>
                            <input type="email" name="email" placeholder={t('contact.email_placeholder')} required />
                        </label>
                    </div>
                    <label className="form-field">
                        <span>{t('contact.subject')}</span>
                        <input type="text" name="subject" placeholder={t('contact.subject_placeholder')} />
                    </label>
                    <label className="form-field">
                        <span>{t('contact.message')}</span>
                        <textarea name="message" rows="5" placeholder={t('contact.message_placeholder')} required />
                    </label>

                    <button type="submit" className="btn btn-blue" disabled={status === 'sending'}>
                        {status === 'sending' ? t('contact.sending') : t('contact.send')}
                        <ArrowRight size={16} className="flip-rtl" />
                    </button>
                    <p className="form-note">{t('contact.note')}</p>
                </form>
            </div>
        </div>
    );
};

const CtaSection = () => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const sectionRef = useRef(null);
    useInViewOnce(sectionRef, { threshold: 0.25 });
    usePointerParallax(sectionRef);
    const callHref = `mailto:${CONTACT.email}?subject=${encodeURIComponent(t('cta.call_subject'))}`;

    return (
        <section className="cta" id="Contact" ref={sectionRef}>
            <img src={glow1} alt="" className="cta-glow cta-glow--1" />
            <img src={glow2} alt="" className="cta-glow cta-glow--2" />

            <div className="container cta-content" data-inview>
                <div className="cta-text">
                    <span className="cta-eyebrow">{t('cta.eyebrow')}</span>
                    <h2 className="cta-title">{t('cta.title')}</h2>
                    <p className="cta-subtitle">{t('cta.subtitle')}</p>
                </div>
                <div className="cta-buttons">
                    <button
                        className="btn btn-blue"
                        onClick={() => {
                            setOpen(true);
                            trackEvent('Contact Form Opened');
                        }}
                    >
                        {t('cta.send')}
                        <ArrowRight size={16} className="flip-rtl" />
                    </button>
                    <a href={callHref} className="btn btn-outline-light" onClick={() => trackEvent('Schedule Call Click')}>
                        <CalendarDays size={18} />
                        {t('cta.schedule')}
                        <ArrowRight size={16} className="flip-rtl" />
                    </a>
                </div>
            </div>

            <div className="paper-plane" dir="ltr" aria-hidden="true" data-inview>
                <FlightTrail style={{ left: 0, top: 56, width: 170, height: 130 }} />
                <img src={glowDot} alt="" className="cta-dot" style={{ left: -14, top: 160, width: 44, height: 44 }} />
                <img src={plane} alt="" className="cta-plane" style={{ left: 120, top: 1.5, width: 150.7, height: 129 }} />
                <p className="plane-note">
                    {t('cta.doodle', { returnObjects: true }).map(line => <span key={line}>{line}</span>)}
                </p>
            </div>

            {open && <ContactModal onClose={() => setOpen(false)} />}
        </section>
    );
};

export default CtaSection;
